<script setup>
import { useApi } from '@/composables/useApi'
import { resolveAnaesthetistName } from '@/utils/resolveAnaesthetistName'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

definePage({
  meta: {
    layout: 'default',
    requiresAuth: true,
    roles: ['secretary_anesthetist'],
  },
})

const router = useRouter()
const anaesthetists = ref([])
const loading = ref(false)
const error = ref('')
const search = ref('')
const actionLoadingId = ref(null)

const headers = [
  { title: 'Anaesthetist', key: 'name', sortable: true },
  { title: 'GMC number', key: 'gmc_number', sortable: true },

  // { title: 'Email', key: 'email', sortable: false },
  // { title: 'Hospitals', key: 'hospitals', sortable: false },
  // { title: 'Specialist areas', key: 'specialist_areas', sortable: false },
  // { title: 'Completeness', key: 'completeness', sortable: false },
  { title: 'Status', key: 'status', sortable: true },
  { title: 'Profile', key: 'actions', sortable: false },
]

const resolveRowItem = item => item?.raw ?? item

const resolveEmail = anaesthetist => anaesthetist?.primary_email || anaesthetist?.user_email || '—'

const resolveStatus = anaesthetist => (anaesthetist?.is_active ? 'Active' : 'Inactive')

const resolveStatusColor = anaesthetist => (anaesthetist?.is_active ? 'success' : 'error')

const resolveHospitalCount = anaesthetist => anaesthetist?.hospitals?.length ?? 0

const resolveAreaCount = anaesthetist => anaesthetist?.specialist_areas?.length ?? 0

const resolveToggleLabel = anaesthetist => (anaesthetist?.is_active ? 'Disabilita' : 'Riabilita')

const resolveToggleColor = anaesthetist => (anaesthetist?.is_active ? 'error' : 'success')

const isDraft = item => {
  const a = resolveRowItem(item)

  return (a?.hospitals?.length ?? 0) === 0 || (a?.specialist_areas?.length ?? 0) === 0
}

const openProfile = anaesthetist => {
  if (!anaesthetist?.id)
    return

  router.push({
    name: 'anaesthetist-id-tab',
    params: {
      id: String(anaesthetist.id),
      tab: 'profile',
    },
  })
}

const toggleAnaesthetistActivation = async anaesthetist => {
  if (!anaesthetist?.id)
    return

  const nextState = !Boolean(anaesthetist?.is_active)
  const actionLabel = nextState ? 'riabilitare' : 'disabilitare'
  const confirmed = window.confirm(`Vuoi davvero ${actionLabel} questo anestesista?`)

  if (!confirmed)
    return

  actionLoadingId.value = anaesthetist.id

  try {
    const { error: toggleError } = await useApi(`/api/secretary-anesthetists/anaesthetists/${anaesthetist.id}/activation`, {
      method: 'PUT',
      body: {
        is_active: nextState,
      },
    })

    if (toggleError.value)
      throw toggleError.value

    await fetchAnaesthetists()
  }
  catch (err) {
    error.value = err?.response?._data?.message
      ?? err?.data?.message
      ?? err?.message
      ?? 'Unable to update the anaesthetist status.'
  }
  finally {
    actionLoadingId.value = null
  }
}

const fetchAnaesthetists = async () => {
  loading.value = true
  error.value = ''

  try {
    const { data, error: fetchError } = await useApi('/api/secretary-anesthetists/anaesthetists')

    if (fetchError.value)
      throw fetchError.value

    const payload = data.value?.data ?? data.value ?? []

    anaesthetists.value = Array.isArray(payload)
      ? payload
      : (Array.isArray(payload?.data) ? payload.data : [])
  }
  catch (err) {
    error.value = err?.response?._data?.message
      ?? err?.data?.message
      ?? err?.message
      ?? 'Unable to load anaesthetists.'
  }
  finally {
    loading.value = false
  }
}

const filteredAnaesthetists = computed(() => {
  const term = search.value.trim().toLowerCase()

  if (!term)
    return anaesthetists.value

  return anaesthetists.value.filter(anaesthetist => {
    const haystack = [
      resolveAnaesthetistName(anaesthetist),
      anaesthetist?.gmc_number,
      resolveEmail(anaesthetist),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()

    return haystack.includes(term)
  })
})

onMounted(fetchAnaesthetists)
</script>

<template>
  <VContainer class="py-6">
    <VCard>
      <VCardTitle class="d-flex align-center justify-space-between flex-wrap ga-2">
        <div>
          <div class="text-h4">
            Associated anaesthetists
          </div>
          <div class="text-body-2 text-medium-emphasis mt-1">
            View the anaesthetists linked to your account and open their profiles.
          </div>
        </div>

        <div class="d-flex align-center ga-2">
          <VTextField
            v-model="search"
            density="compact"
            hide-details
            variant="outlined"
            prepend-inner-icon="ri-search-line"
            label="Search"
            style="min-inline-size: 220px;"
          />
          <VBtn
            variant="text"
            prepend-icon="ri-refresh-line"
            :loading="loading"
            @click="fetchAnaesthetists"
          >
            Refresh
          </VBtn>
          <VBtn
            color="primary"
            prepend-icon="ri-user-add-line"
            @click="router.push({ name: 'anaesthetist-id-tab', params: { id: 'new', tab: 'profile' } })"
          >
            New Anaesthetist
          </VBtn>
        </div>
      </VCardTitle>
      <VDivider />
      <VCardText>
        <VAlert
          v-if="error"
          type="error"
          class="mb-4"
        >
          {{ error }}
        </VAlert>

        <VProgressLinear
          v-if="loading"
          indeterminate
          class="mb-4"
        />

        <VDataTable
          :headers="headers"
          :items="filteredAnaesthetists"
          item-key="id"
          class="text-no-wrap"
        >
          <template #item.name="{ item }">
            {{ resolveAnaesthetistName(resolveRowItem(item)) }}
          </template>

          <template #item.gmc_number="{ item }">
            {{ resolveRowItem(item)?.gmc_number || '—' }}
          </template>

          <template #item.email="{ item }">
            {{ resolveEmail(resolveRowItem(item)) }}
          </template>

          <template #item.hospitals="{ item }">
            <VChip
              size="small"
              variant="tonal"
            >
              {{ resolveHospitalCount(resolveRowItem(item)) }}
            </VChip>
          </template>

          <template #item.specialist_areas="{ item }">
            <VChip
              size="small"
              variant="tonal"
            >
              {{ resolveAreaCount(resolveRowItem(item)) }}
            </VChip>
          </template>

          <template #item.completeness="{ item }">
            <VChip
              v-if="isDraft(item)"
              size="small"
              color="warning"
              variant="tonal"
              prepend-icon="ri-error-warning-line"
            >
              Draft
            </VChip>
            <VIcon
              v-else
              color="success"
              icon="ri-checkbox-circle-line"
            />
          </template>

          <template #item.status="{ item }">
            <VChip
              size="small"
              :color="resolveStatusColor(resolveRowItem(item))"
              variant="tonal"
            >
              {{ resolveStatus(resolveRowItem(item)) }}
            </VChip>
          </template>

          <template #item.actions="{ item }">
            <div class="d-flex align-center ga-2">
              <VBtn
                size="small"
                variant="tonal"
                :disabled="!resolveRowItem(item)?.id || actionLoadingId === resolveRowItem(item)?.id"
                :loading="actionLoadingId === resolveRowItem(item)?.id"
                :color="resolveToggleColor(resolveRowItem(item))"
                @click.stop="toggleAnaesthetistActivation(resolveRowItem(item))"
              >
                {{ resolveToggleLabel(resolveRowItem(item)) }}
              </VBtn>

              <VBtn
                size="small"
                :disabled="!resolveRowItem(item)?.id"
                @click.stop="openProfile(resolveRowItem(item))"
              >
                View profile
              </VBtn>
            </div>
          </template>

          <template #no-data>
            <div class="text-body-2 text-medium-emphasis">
              No anaesthetists are currently linked to your account.
            </div>
          </template>
        </VDataTable>
      </VCardText>
    </VCard>
  </VContainer>
</template>
